// /src/components/layout/Header.jsx
// VERSÃO "SENTINELA DE ELITE" - PROTOCOLO DE PERCEPÇÃO 100% (BASE 0.7)

import React, { useState, useEffect } from 'react';
import { 
  FiMenu, FiX, FiShield, FiGrid, 
  FiPlus, FiMinus, FiEye, FiDroplet,
  FiBell, FiBellOff
} from 'react-icons/fi';
import EliteThemeSwitcher from './EliteThemeSwitcher';

const NotificationToggle = () => {
  const [perm, setPerm] = useState(Notification.permission);

  const requestPerm = async () => {
    if (perm !== 'granted') {
      const result = await Notification.requestPermission();
      setPerm(result);
      if (result === 'granted') {
        new Notification("Radar Ativo!", { body: "Permissão concedida. Você receberá alertas aqui.", icon: '/vite.svg' });
      }
    } else {
      new Notification("Radar Operante", { body: "As notificações do Chrome estão funcionando perfeitamente.", icon: '/vite.svg' });
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button 
        onClick={requestPerm} 
        className={`p-2 rounded-xl transition-all border ${perm === 'granted' ? 'text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10' : 'text-red-500 border-red-500/50 bg-red-500/10 animate-pulse'}`} 
        title={perm === 'granted' ? 'Notificações Ativas (Clique p/ testar)' : 'Habilitar Notificações do Chrome'}
      >
        {perm === 'granted' ? <FiBell size={16} /> : <FiBellOff size={16} />}
      </button>
    </div>
  );
};

const PwaInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  if (!deferredPrompt) return null;

  return (
    <button 
      onClick={async () => {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') setDeferredPrompt(null);
      }}
      className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-[10px] uppercase tracking-widest px-3 py-2 rounded-xl shadow-[0_0_15px_rgba(16,185,129,0.5)] transition-all animate-pulse"
    >
      Instalar App
    </button>
  );
};

/**
 * 🛰️ CONTROLADOR DE VISÃO (VISION ENGINE)
 * O segredo: 0.7 real = 100% Pro Cliente
 */
const VisionScaleController = () => {
  const [zoom, setZoom] = useState(0.7);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleZoom = (newZoom) => {
    const fixedZoom = parseFloat(newZoom.toFixed(2));
    setZoom(fixedZoom);
    document.documentElement.style.setProperty('--mos-zoom', fixedZoom);
  };

  // Cálculo de percepção para o cliente (0.7 -> 100%)
  const displayPercent = Math.round((zoom / 0.7) * 100);

  return (
    <div className={`flex items-center gap-1 bg-black/20 p-1 rounded-2xl border border-white/5 transition-all duration-500 ${isExpanded ? 'px-2' : 'w-12 justify-center'}`}>
      
      {isExpanded && (
        <>
          <button onClick={() => handleZoom(Math.max(0.4, zoom - 0.07))} className="p-1.5 hover:bg-[var(--accent-color)] hover:text-black rounded-lg transition-all text-[var(--text-secondary)]">
            <FiMinus size={12} />
          </button>
          <div className="flex flex-col items-center min-w-[45px] border-x border-white/10 px-2">
            <span className="text-[6px] font-black uppercase opacity-40">Tamanho</span>
            <span className="text-[10px] font-bold text-[var(--accent-color)]">{displayPercent}%</span>
          </div>
          <button onClick={() => handleZoom(Math.min(1.2, zoom + 0.07))} className="p-1.5 hover:bg-[var(--accent-color)] hover:text-black rounded-lg transition-all text-[var(--text-secondary)]">
            <FiPlus size={12} />
          </button>
        </>
      )}

      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className={`p-2 rounded-xl transition-all ${isExpanded ? 'bg-[var(--accent-color)] text-black' : 'text-[var(--accent-color)] hover:bg-white/5'}`}
        title="Ajustar Visão"
      >
        <FiEye size={16} />
      </button>
    </div>
  );
};

export default function Header({ toggleSidebar, sidebarMode }) {
  const [showThemes, setShowThemes] = useState(false);

  const renderIcon = () => {
    if (sidebarMode === 0) return <FiMenu size={20} />;
    if (sidebarMode === 1) return <FiGrid size={20} />;
    return <FiX size={20} />;
  };

  return (
    <header 
      className="flex-shrink-0 flex justify-between items-center p-4 transition-all duration-500 h-20 border-b z-[50]"
      style={{ 
        backgroundColor: 'var(--bg-card)', 
        borderColor: 'var(--border-color)',
        color: 'var(--text-primary)'
      }}
    >
      {/* 🕹️ LADO ESQUERDO: COMANDO LATERAL */}
      <div className="flex items-center gap-4">
        <button
          onClick={toggleSidebar}
          className={`p-3 rounded-2xl transition-all duration-300 hover:scale-110 active:scale-95 border relative group ${
            sidebarMode === 0 ? 'animate-pulse shadow-[0_0_15px_rgba(var(--accent-rgb),0.4)]' : ''
          }`}
          style={{ 
            backgroundColor: 'var(--bg-main)', 
            borderColor: sidebarMode === 0 ? 'var(--accent-color)' : 'var(--border-color)',
            color: 'var(--accent-color)' 
          }}
        >
          {renderIcon()}
          {sidebarMode === 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-[var(--accent-color)] rounded-full border-2 border-[var(--bg-card)]"></span>
          )}
        </button>
        
        <div className="hidden md:flex flex-col">
          <span className="text-[10px] font-black uppercase tracking-[0.3em] opacity-40">MOS Gold System</span>
          <span className="text-xs font-bold uppercase tracking-widest flex items-center gap-2">
            <FiShield className={`${sidebarMode === 0 ? 'text-red-500' : 'text-accent'} animate-pulse`} size={10} /> 
            {sidebarMode === 0 ? 'Furtividade Ativa' : 'Terminal Operacional'}
          </span>
        </div>
      </div>

      {/* 💎 LADO DIREITO: CENTRAL DE ESTAÇÕES */}
      <div className="flex items-center gap-3 sm:gap-4">
        
        {/* Controle de Visão (Expandível) */}
        <VisionScaleController />

        {/* Botão de Instalar PWA */}
        <PwaInstallButton />

        {/* Toggle de Notificações Nativas do Chrome */}
        <NotificationToggle />

        {/* Seletor de Temas (Expandível) */}
        <div className={`flex items-center bg-black/20 p-1 rounded-2xl border border-white/5 transition-all duration-500 ${showThemes ? 'px-2' : 'w-12 justify-center'}`}>
          {showThemes && (
            <div className="mr-2 animate-in fade-in slide-in-from-right-2 duration-300">
              <EliteThemeSwitcher />
            </div>
          )}
          <button 
            onClick={() => setShowThemes(!showThemes)}
            className={`p-2 rounded-xl transition-all ${showThemes ? 'bg-[var(--accent-color)] text-black' : 'text-[var(--accent-color)] hover:bg-white/5'}`}
          >
            <FiDroplet size={16} />
          </button>
        </div>

        {/* PERFIL IMPERIAL */}
        <div className="flex items-center gap-3 pl-4 border-l border-white/10">
          <div className="hidden lg:flex flex-col text-right">
            <span className="text-[8px] font-black uppercase opacity-30">Operador</span>
            <span className="text-[10px] font-bold uppercase tracking-tighter">Master MOS</span>
          </div>
          <div className="h-10 w-10 rounded-xl border-2 flex items-center justify-center font-black text-[10px] shadow-xl transition-all hover:rotate-12 cursor-pointer group overflow-hidden relative"
               style={{ 
                 backgroundColor: 'var(--bg-main)',
                 borderColor: 'var(--accent-color)', 
                 color: 'var(--accent-color)' 
               }}>
            <span className="group-hover:opacity-0 transition-opacity">GOLD</span>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-[var(--accent-color)] text-black font-black">
               MOS
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}